import React, { useState } from 'react';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';
import { BsTrash } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import * as actions from '../../Redux/Actions/index';
import { showErrorToastMessage, showSuccessToastMessage } from '../Toasts/Toasts';
import ReassignmentModal from './ReassignmentModal'; 

interface IManagerProps {
  show: boolean;
  onHide: () => void;
  data: { _id: string; name: string }[]; 
  refreshData: () => void;
  allTasksOptions: { value: string; label: string }[]; 
  onReassignSuccess?: (oldId: string, newTarget: any) => void;
}

const TaskNameManagerModal = ({ show, onHide, data, refreshData, allTasksOptions, onReassignSuccess }: IManagerProps) => {
  const dispatch = useDispatch();
  
  const [itemToDelete, setItemToDelete] = useState<{id: string, name: string} | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [reassignData, setReassignData] = useState<any>(null);

  const initiateDelete = (id: string, name: string) => {
    setItemToDelete({ id, name });
  };

  const confirmDelete = () => {
    if(!itemToDelete) return;
    setIsDeleting(true);

    // Pass null for transferTo initially
    dispatch(actions.deleteTaskNameAction(itemToDelete.id, null, (res: any) => {
        setIsDeleting(false);

        // 1. Success
        if (res?.success || res?.code === 200 || res?.data?.success) {
            showSuccessToastMessage("Task deleted successfully.");
            refreshData();
            setItemToDelete(null); 
        } 
        
        // 2. Handle 412 (Data exists -> Move required)
        else if (
            res?.error?.response?.status === 412 ||
            res?.code === 412 ||
            res?.error?.http_code === 412
        ) {
             setItemToDelete(null); // Close Confirm

             // Extract Count
             const errorData = res.meta_data || res?.error?.response?.data?.meta_data || res?.error?.meta_data;
             const count = Array.isArray(errorData) ? errorData.length : 0;

             setReassignData({ 
                id: itemToDelete.id, 
                name: itemToDelete.name, 
                count: count
            });
        } 
        // 3. Generic Error
        else {
            const errorMsg = res?.error?.response?.data?.error?.error_description || "Delete failed.";
            showErrorToastMessage(errorMsg);
            setItemToDelete(null);
        }
    }));
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton><Modal.Title>Manage Tasks</Modal.Title></Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {data && data.length > 0 ? (
                <Table hover responsive>
                    <tbody>
                        {data.map((item) => (
                            <tr key={item._id}>
                                <td className="align-middle">{item.name}</td>
                                <td className="text-end">
                                    <Button variant="outline-danger" size="sm" onClick={() => initiateDelete(item._id, item.name)}>
                                        <BsTrash />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : <p className="text-center text-muted">No Tasks found.</p>}
        </Modal.Body>
      </Modal>

      <Modal show={!!itemToDelete} onHide={() => setItemToDelete(null)} centered size="sm">
        <Modal.Header closeButton><Modal.Title>Confirm Delete</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?</Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setItemToDelete(null)} disabled={isDeleting}>No</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={isDeleting}>
                {isDeleting ? <Spinner size="sm" animation="border" /> : "Yes, Delete"}
            </Button>
        </Modal.Footer>
      </Modal>

      {/* Reassignment Modal */}
      {reassignData && (
          <ReassignmentModal 
            show={!!reassignData}
            onHide={() => setReassignData(null)}
            conflictedItem={reassignData}
            type="TASK"
            onSuccess={(target: any) => {
                refreshData();
                if(onReassignSuccess) onReassignSuccess(reassignData.id, target);
                setReassignData(null);
            }}
          />
      )}
    </>
  );
};

export default TaskNameManagerModal;